import { configuration } from './configuration';
import * as querystring from 'querystring';
import * as _ from 'lodash';
import { getStorage } from './storageService';
import moment from 'moment';
import { hasCookie } from '../ui/components/SessionProvider';

async function get(call, params) {
    let url = configuration.FLAKE_BACK_END + call;
    const asUser = call.includes('/api') ? undefined : getStorage('as_user');
    const getParams = {
        ...(params ? params : {}),
        ...(asUser && hasCookie() ? { as_user: asUser } : {})
    };
    if (Object.keys(getParams).length > 0) {
        url += '?' + querystring.stringify(getParams);
    }
    const response = await fetch(url, {
        credentials: 'include'
    });
    if (response.status !== 200) {
        let data = await response.text();
        try {
            data = JSON.parse(data).message;
        } catch (exception) {}
        throw new Error(data);
    }
    return await response.json();
}

async function post(call, payload) {
    let url = configuration.FLAKE_BACK_END + call;
    const asUser = getStorage('as_user');
    if (asUser && hasCookie()) {
        url += '?' + querystring.stringify({ as_user: asUser });
        const answer = window.confirm('Are you sure to do this action?');
        if (answer) {
            return await doCall('POST', url, payload);
        }
    } else {
        return await doCall('POST', url, payload);
    }
}

async function doCall(method, url, payload) {
    const response = await fetch(url, {
        method,
        credentials: 'include',
        ...(payload ? { body: JSON.stringify(payload) } : {})
    });
    if (response.status !== 200) {
        let data = await response.text();
        try {
            data = JSON.parse(data).message;
        } catch (exception) {}
        throw new Error(data);
    }
    return await response.json();
}

async function put(call, payload) {
    let url = configuration.FLAKE_BACK_END + call;
    const asUser = getStorage('as_user');
    if (asUser && hasCookie()) {
        url += '?' + querystring.stringify({ as_user: asUser });
        const answer = window.confirm('Are you sure to do this action?');
        if (answer) {
            return await doCall('PUT', url, payload);
        }
    } else {
        return await doCall('PUT', url, payload);
    }
}

export async function registerUser(
    email,
    password,
    firstName,
    lastName,
    company,
    phone
) {
    const response = await fetch(configuration.FLAKE_BACK_END + '/user', {
        method: 'POST',
        cors: true,
        body: JSON.stringify({
            email,
            password,
            firstName,
            lastName,
            company,
            phone
        })
    });
    if (response.status !== 200) {
        let data = await response.text();
        try {
            data = JSON.parse(data).message;
        } catch (exception) {}
        throw new Error(data);
    }
    return await response.json();
}

export async function resendValidationMail(email) {
    return await get('/user/resend-validation', {
        email
    });
}

export async function validate(token) {
    return await get('/user/validate', {
        token
    });
}

export async function login(email, password) {
    return get('/login', { email, password });
}

export async function logout() {
    return get('/logout', {});
}

export async function getKeys(offset, limit, sort, text) {
    return get('/keys', {
        ...(text ? { text } : {}),
        ...(sort ? { sort } : {}),
        ...(offset ? { offset } : {}),
        ...(limit ? { limit } : {})
    });
}

export async function getKey(id) {
    return get('/key', { id });
}

export async function createKey(name, tags, testMode) {
    return post('/key', { name, tags, testMode });
}

export async function getLogs(offset, limit, sort, text, filters) {
    // Text is not used
    try {
        return await get('/logs', {
            ...(filters && filters.id ? { id: filters.id } : {}),
            ...(sort ? { sort } : {}),
            ...(offset ? { offset } : {}),
            ...(limit ? { limit } : {}),
            ...(filters && filters.requests ? { requests: filters.requests } : {}),
            ...(filters && filters.key ? { key: filters.key } : {})
        });
    } catch {
        return {
            docs: [],
            total: 0,
            offset,
            limit,
            page: 1,
            pages: 1
        };
    }
}

export async function getLog(id) {
    return get('/log', { id });
}

export async function getExecution(id) {
    return get('/execution', { id });
}

export async function getLogStats(
    keys,
    requests,
    statuses,
    startDate,
    endDate
) {
    const response = await get('/log/stats', {
        ...(keys ? { keys } : {}),
        ...(requests ? { requests } : {}),
        ...(statuses ? { statuses } : {}),
        ...(startDate ? { startDate: startDate.toString() } : {}),
        ...(endDate ? { endDate } : {})
    });
    response.size = response.values.length;
    response.values = prepareStats(response, startDate, endDate);
    return response;
}

export async function getUsageStats(startDate, endDate) {
    return await get('/log/usage', {
        ...(startDate ? { startDate: startDate.toString() } : {}),
        ...(endDate ? { endDate: endDate.toString() } : {})
    });
}

function prepareStats(response, startDate, endDate) {
    const { periodicity } = response;
    let offset, margin;
    let time = new Date(startDate);
    if (periodicity === 'daily') {
        offset = 24 * 3600 * 1000;
        margin = 3 * 3600 * 1000;
        time.setHours(0, 0, 0, 0);
    } else if (periodicity === 'hourly') {
        offset = 3600 * 1000;
        margin = 0.2 * 3600 * 1000;
        time.setHours(time.getHours(), 0, 0, 0);
    } else if (periodicity === 'minutely') {
        offset = 1000;
        margin = 0.2 * 1000;
        time.setHours(time.getHours(), time.getMinutes(), 0, 0);
    }
    let values = _.sortBy(response.values, 'date');
    values.forEach(entry => {
        entry.date = new Date(entry.date);
    });
    const result = [];
    time = time.getTime();
    const endTime = (endDate || new Date()).getTime();
    let index = 0;
    while (time < endTime) {
        if (
            index < values.length &&
      Math.abs(values[index].date.getTime() - time) < margin
        ) {
            result.push(values[index++]);
        } else {
            result.push({ date: new Date(time) });
        }
        time += offset;
    }
    return result;
}

export async function getQuota(key) {
    return get('/quota', { key });
}

export async function getResetPassword(email) {
    return get('/user/reset-password', { email });
}

export async function getNewPasswordValidate(token) {
    return get('/user/new-password/validate', { token });
}

export async function postNewPassword(token, password) {
    return post('/user/new-password', { token, password });
}

export async function getUserQuota(userId) {
    return get(`/quota/${userId}`, {});
}

export async function putKey(data) {
    return put('/key', data);
}

export async function postQuota(data) {
    return post('/quota', data);
}

export async function restoreUser(userId) {
    return post('/user/restore', { user: userId });
}

export async function getUsers(text, offset, limit, sort, deleted) {
    const response = await get('/users', {
        ...(text ? { text } : {}),
        ...(sort ? { sort } : {}),
        ...(offset ? { offset } : {}),
        ...(limit ? { limit } : {}),
        ...(deleted ? { deleted } : {})
    });
    return response;
}

export async function getUsersWithStats(
    text,
    offset,
    limit,
    sort,
    days,
    deleted
) {
    const response = await getUsers(text, offset, limit, sort, deleted);
    const newDocs = await Promise.all(
        response.docs.map(async user => {
            if (!user.deleted) {
                const responseStats = await get('/log/stats', {
                    startDate: moment()
                        .subtract(days, 'days')
                        .toDate()
                        .toString(),
                    as_user: user.id
                });
                const returnValue = responseStats.values.reduce(
                    (prev, next) => prev + next.count,
                    0
                );
                return { ...user, stats: returnValue };
            }
            return user;
        })
    );
    return { ...response, docs: newDocs };
}

/**
 * Returns the details of the current user
 */
export async function getUserDetails(userId) {
    if (userId === undefined) {
        return get('/user/details', {});
    } else {
        return get(`/user/details/${userId}`, {});
    }
}

export async function softDeleteUser(userId) {
    const answer = window.confirm('Are you sure to do this action?');
    if (answer) {
        return await doCall(
            'DELETE',
            `${configuration.FLAKE_BACK_END}/user?user=${userId}`,
            {}
        );
    }
}

export function placesSummary(key) {
    return get('/api/v1/places_profiler/summary', { key });
}
